import { useCanvasFileRecords } from "@/api/fileRecords";
import { useCanvasNodesMirrorState } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import { useLocalStorageState } from "ahooks";
import { useMemo } from "react";
import {
  type ContentCategory,
  type ContentsViewMode,
  buildAllContentItems,
  buildFileRecordLookups,
  buildSectionView,
  buildVisibleItemIdOrder,
  filterVisibleItems,
  groupItemsByType,
} from "./canvasRailContentsModel";

export function useCanvasRailContents(
  canvasId: string,
  searchQuery: string,
  options?: { filesOnly?: boolean },
) {
  const [viewMode, setViewMode] = useLocalStorageState<ContentsViewMode>(
    "canvas-explorer.canvas.contents.viewMode",
    { defaultValue: "section" },
  );
  const activeViewMode: ContentsViewMode = viewMode ?? "section";

  const [optionalFilters, setOptionalFilters] = useLocalStorageState<
    Array<"entity" | "object">
  >("canvas-explorer.canvas.contents.filter", {
    defaultValue: ["entity"],
  });

  const activeFilter = useMemo(() => {
    if (options?.filesOnly) return new Set<ContentCategory>(["file"]);
    const set = new Set<ContentCategory>(["file"]);
    for (const f of optionalFilters ?? ["entity"]) set.add(f);
    return set;
  }, [optionalFilters, options?.filesOnly]);

  const toggleOptionalFilter = (cat: "entity" | "object") => {
    const current = new Set<"entity" | "object">(optionalFilters ?? ["entity"]);
    if (current.has(cat)) current.delete(cat);
    else current.add(cat);
    setOptionalFilters(Array.from(current));
  };

  const {
    data: fileRecords,
    isLoading: filesLoading,
    isFetched: filesFetched,
  } = useCanvasFileRecords(canvasId || undefined);

  const { nodes: rawNodes, nodesSynced } = useCanvasNodesMirrorState(canvasId);

  const resolvedFileRecords = fileRecords ?? [];
  const resolvedRawNodes = rawNodes;

  const isContentsLoading =
    !!canvasId &&
    (!filesFetched ||
      !nodesSynced ||
      (filesLoading && resolvedFileRecords.length === 0));

  const { fileRecordIds, nodesById, fileRecordIdToNodeId, fileRecordNodeIds } =
    useMemo(
      () => buildFileRecordLookups(resolvedFileRecords, resolvedRawNodes),
      [resolvedFileRecords, resolvedRawNodes],
    );

  const allItems = useMemo(
    () =>
      buildAllContentItems({
        fileRecords: resolvedFileRecords,
        rawNodes: resolvedRawNodes,
        fileRecordIdToNodeId,
        fileRecordNodeIds,
        nodesById,
      }),
    [
      resolvedFileRecords,
      resolvedRawNodes,
      fileRecordIdToNodeId,
      fileRecordNodeIds,
      nodesById,
    ],
  );

  const visibleItems = useMemo(
    () => filterVisibleItems(allItems, activeFilter, searchQuery),
    [allItems, activeFilter, searchQuery],
  );

  const visibleTotal = visibleItems.length;

  const typeGroups = useMemo(
    () => groupItemsByType(visibleItems),
    [visibleItems],
  );

  const sectionView = useMemo(
    () =>
      buildSectionView({
        rawNodes: resolvedRawNodes,
        visibleItems,
        searchQuery,
      }),
    [resolvedRawNodes, visibleItems, searchQuery],
  );

  const headerCount =
    activeViewMode === "section" ? sectionView.totalVisible : visibleTotal;

  const hasSectionViewBody =
    activeViewMode === "section" &&
    (sectionView.entries.length > 0 || sectionView.rootItems.length > 0);

  const visibleItemIdOrder = useMemo(
    () =>
      buildVisibleItemIdOrder({
        activeViewMode,
        visibleItems,
        sectionView,
      }),
    [activeViewMode, visibleItems, sectionView],
  );

  const isEmpty =
    !isContentsLoading && allItems.length === 0 && searchQuery.length === 0;

  return {
    searchQuery,
    activeViewMode,
    setViewMode,
    optionalFilters: optionalFilters ?? ["entity"],
    toggleOptionalFilter,
    activeFilter,
    fileRecords: resolvedFileRecords,
    rawNodes: resolvedRawNodes,
    fileRecordIds,
    nodesById,
    fileRecordIdToNodeId,
    fileRecordNodeIds,
    allItems,
    visibleItems,
    visibleTotal,
    typeGroups,
    sectionView,
    headerCount,
    hasSectionViewBody,
    visibleItemIdOrder,
    isContentsLoading,
    isEmpty,
  };
}
